import { ReactNode } from "react";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { UserProvider } from "../../contexts/UserContext";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import FullLoading from "../FullLoading";
import { OrganizationType } from "../../__generated__/graphql";
import SwitchOrganization from "../../pages/switch-organization";
import useCurrentUserOrganizations from "../../hooks/useCurrentUserOrganizations";

interface ProtectedRouteProps {
  children: ReactNode;
}

function ProtectedRouteContent({ children }: ProtectedRouteProps) {
  const {
    // isPartOfOrganization,
    isUserContextLoading,
    currentOrganization,
    organizations,
  } = useUser();
  const { loading: isOrganizationLoading, isPartOfOrganization } =
    useCurrentUserOrganizations();

  // If connected, check if there is an invitation code in the local storage
  const invitationCode = localStorage.getItem("invitationCode");

  // For organization member invitation, this data is stored in the local storage
  const invitedToOrganizationId = localStorage.getItem(
    "invitedToOrganizationId"
  );

  // Wait for loading to complete and ensure isPartOfOrganization has a definitive value
  if (
    isUserContextLoading ||
    isOrganizationLoading ||
    isPartOfOrganization === undefined
  ) {
    return <FullLoading />;
  }

  if (isPartOfOrganization === false) {
    if (invitedToOrganizationId && invitationCode) {
      return (
        <Navigate
          to={`/join-organization?organizationId=${invitedToOrganizationId}&invitationCode=${invitationCode}`}
          replace
        />
      );
    }

    return <Navigate to="/create-organization" replace />;
  }

  if (invitationCode) {
    return <Navigate to={`/sharing-invitation/${invitationCode}`} replace />;
  }

  if (
    currentOrganization?.organizationType === OrganizationType.Artist &&
    !localStorage.getItem("continueToWebApp")
  ) {
    return <Navigate to="/download-app" replace />;
  }

  if (!currentOrganization && organizations && organizations.length > 0) {
    return <SwitchOrganization />;
  }

  return <>{children}</>;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthLoading, isAuthenticated } = useAuth();

  if (isAuthLoading) {
    return <FullLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <UserProvider>
      <ProtectedRouteContent>{children}</ProtectedRouteContent>
    </UserProvider>
  );
}
